import {Component, computed, effect, input, output, signal} from '@angular/core';
import {TranslatePipe} from '@ngx-translate/core';
import {ElciaButton} from '../buttons/button/button';
import {Dot} from '../dot/dot';

export type SaveBarState = 'hidden' | 'pending' | 'invalid' | 'saved';

// How long the success state stays on screen before the bar folds away
const CONFIRMATION_DURATION_MS = 2_500;

const DOT_COLOR: Record<SaveBarState, string> = {
  hidden: 'transparent',
  pending: '#fbbf24', // amber-400
  invalid: '#fb7185', // rose-400
  saved: '#ffffff',
};

const MESSAGE: Record<SaveBarState, string> = {
  hidden: '',
  pending: 'app.unsaved-changes',
  invalid: 'app.fields-to-fix',
  saved: 'app.changes-saved',
};

@Component({
  selector: 'elcia-save-bar',
  imports: [TranslatePipe, ElciaButton, Dot],
  templateUrl: './save-bar.html',
  styleUrl: './save-bar.scss',
})
export class SaveBar {

  isDirty = input.required<boolean>();
  isInvalid = input(false);
  isSaving = input(false);
  saveLabel = input('action.save');

  // Set by the parent on every successful save; each new value replays the confirmation state
  savedAt = input<Date | null>(null);

  onSave = output();
  onDiscard = output();

  // The invalid state only surfaces once the user has tried to save, so a form
  // that is merely half-filled is never flagged while it is still being typed in
  private readonly hasTriedToSave = signal(false);
  private readonly showsConfirmation = signal(false);

  protected readonly state = computed<SaveBarState>(() => {
    if (this.isDirty() || this.isSaving()) {
      return this.hasTriedToSave() && this.isInvalid() ? 'invalid' : 'pending';
    }
    return this.showsConfirmation() ? 'saved' : 'hidden';
  });

  protected readonly message = computed(() => MESSAGE[this.state()]);
  protected readonly dotColor = computed(() => DOT_COLOR[this.state()]);

  // Saved or discarded: the next attempt starts from a clean slate
  private readonly clearSaveAttempt = effect(() => {
    if (!this.isDirty()) this.hasTriedToSave.set(false);
  });

  private readonly confirmSave = effect(onCleanup => {
    if (this.savedAt() == null) return;

    this.showsConfirmation.set(true);
    const timer = setTimeout(() => this.showsConfirmation.set(false), CONFIRMATION_DURATION_MS);
    onCleanup(() => clearTimeout(timer));
  });

  protected requestSave() {
    this.hasTriedToSave.set(true);
    this.onSave.emit();
  }
}
